import { useState, useEffect } from 'react';
import { Divider, Grid, Typography } from '@material-ui/core';
import { useDocumentStore } from '@roc/feature-documents';
import { observer } from 'mobx-react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useDrawsStore } from '../hooks';
import { Button, createSelectField, NewFileUploadArea, FileUpload } from '@roc/ui';
import {
  StandardDialog,
} from '@roc/ui';
import { LoanProcess } from '@roc/feature-utils';
import { TaskV2Type, useGeneralTasksStore } from '@roc/feature-general-tasks';
import { DRAWS_PROCESS } from '../constants/draws';

const useStyles = makeStyles((theme: Theme) => {
  return {
    button: {
      marginRight: theme.spacing(1)
    },
    subtitle: {
      color: 'rgba(113, 113, 122, 1)',
      paddingLeft: 8
    },
    title: {
      paddingLeft: 8
    },
    formContainer: {
      marginTop: -40,
    },
    dialogContentclasses: {
      paddingLeft: 0
    }
  };
});

const useDialogContentStyles = makeStyles(theme => ({
  root: {
    paddingLeft: 0,
    paddingRight: 0,
    '& .MuiGrid-container': {
      paddingRight: 0,
    }
  },
}));

export const AddDocumentToDrawForm = observer(({ store, loanDetails, handleClose, properties, drawList, drawSelectedView = false }) => {
  const classes = useStyles();
  const { documentStore } = useDocumentStore();
  const { loanTodosStore } = documentStore;
  const { generalTasksStore } = useGeneralTasksStore();
  const { editTaskStore } = generalTasksStore;
  const [drawsByProperty, setDrawsByProperty] = useState([]);

  const isTaskFrameworkEnabled = !!loanDetails?.taskFrameworkEnabled;

  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const formStore = store.drawFormStore;
  let propertyOptions = [];
  if (!drawSelectedView) {
    propertyOptions = properties.map(property => ({
      label: property.address,
      value: property.propertyId,
    }));
  }

  const selectedDrawId = formStore.form.fields.drawId.value;
  const catalogSections = (editTaskStore.loanTaskSectionNames ?? [])
    .filter(
      category =>
        !category?.loanProcess ||
        category.loanProcess.toLowerCase() === DRAWS_PROCESS.toLowerCase()
    )
    .map(category => ({
      sectionName: category?.displayName ?? category?.category,
      sectionId: category?.taskCategoryId,
      categoryDB: category?.category,
    }));

  const todoSections = (loanTodosStore.todosByLoanProcess[LoanProcess.DRAWS] ?? [])
    .filter(
      todo =>
        !todo?.document?.drawId ||
        String(todo.document.drawId) === String(selectedDrawId)
    )
    .map(todo => ({
      sectionName:
        todo?.category ?? todo?.document?.sectionName ?? todo?.categoryDB,
      sectionId: todo?.document?.sectionId,
      categoryDB: todo?.categoryDB,
    }));

  const documentSections = (documentStore.documentSections ?? [])
    .filter(doc => doc.loanProcess === DRAWS_PROCESS)
    .map(doc => ({
      sectionName: doc.sectionName,
      sectionId: doc.sectionId,
      categoryDB: null,
    }));

  const drawSections = [catalogSections, todoSections, documentSections].find(
    sections => sections.some(section => section.sectionName)
  ) ?? [];

  const drawDocumentSections = drawSections.reduce((options, section) => {
    if (
      section.sectionName &&
      !options.some(option => option.value === section.sectionName)
    ) {
      options.push({
        label: section.sectionName,
        value: section.sectionName,
        sectionId: section.sectionId,
        categoryDB: section.categoryDB,
      });
    }
    return options;
  }, []);

  const onSectionDocumentsUpload = (files: FileUpload[]) => {
    formStore.runFormValidation();
    if (formStore.form.meta.isValid) {
      const sectionName = formStore.form.fields.sectionId.value;
      const section = drawDocumentSections.find(
        option => option.value === sectionName
      );
      const drawId = formStore.form.fields.drawId.value;
      documentStore.uploadSectionDocuments(
        files,
        loanDetails?.loanId,
        section?.sectionId,
        sectionName,
        LoanProcess.DRAWS,
        () => {
          documentStore.fetchDrawDocuments(loanDetails?.loanId, drawId);
          if (isTaskFrameworkEnabled) {
            loanTodosStore.fetchTodos(loanDetails?.loanId, LoanProcess.DRAWS, TaskV2Type.DRAW_ID, drawId);
          }
        },
        drawId,
        null,
        section?.categoryDB ? section.categoryDB : undefined
      );
      handleClose();
    }
  };
  const filterDrawsByProperty = (propertyId) => {
    if (drawList && propertyId) {
      const filteredDraws = drawList.filter(draw => draw.propertyId === propertyId);
      const drawOptions = filteredDraws.map(draw => ({
        label: `Draw # ${draw.drawNumber}`,
        value: draw.drawId,
      }));
      setDrawsByProperty(drawOptions);
    }
  }
  useEffect(() => {
    const drawId = formStore.form.fields.drawId.value;
    if (!drawId) return;
    formStore.onFieldChange('sectionId', '');
    editTaskStore.fetchLoanTaskSectionNames(Number(loanDetails?.loanId), DRAWS_PROCESS);
    documentStore.fetchDrawDocuments(loanDetails?.loanId, drawId);
    if (isTaskFrameworkEnabled) {
      loanTodosStore.fetchTodos(loanDetails?.loanId, LoanProcess.DRAWS, TaskV2Type.DRAW_ID, drawId);
    }
  }, [formStore.form.fields.drawId.value, isTaskFrameworkEnabled])

  useEffect(() => {
    if (formStore.form.fields.propertyId.value) {
      filterDrawsByProperty(formStore.form.fields.propertyId.value);
    }

  }, [formStore.form.fields.propertyId.value]);

  return (
    <Grid className={classes.formContainer} container direction='row' spacing={2}>
      {!drawSelectedView && <Grid item xs={12} container direction='row' spacing={1}>
        <Grid item xs={4}>
          {createSelectField({
            store: formStore,
            testId: 'propertyId',
            fieldName: 'propertyId',
            label: 'Property',
            options: propertyOptions,
          })}
        </Grid>
        <Grid item xs={4}>
          {createSelectField({
            store: formStore,
            testId: 'drawId',
            fieldName: 'drawId',
            label: 'Draw Number',
            options: drawsByProperty,
          })}
        </Grid>
        <Grid item xs={4}>
          {createSelectField({
            store: formStore,
            testId: 'sectionId',
            fieldName: 'sectionId',
            label: 'Section',
            options: drawDocumentSections ?? [],
          })}
        </Grid>
      </Grid>}
      <Grid item xs={12}>
        <NewFileUploadArea
          items={uploads}
          setItems={(items) => setUploads(items)}
          uploadVersion={2}
          showSimulateDeleteButton={true}
          multiple={true}
        />
      </Grid>
      <Grid container justifyContent='flex-end'>
        <Button
          variant='outlined'
          color='primary'
          onClick={() => { handleClose() }}
          testId='removeProperty'
          className={classes.button}
          style={{ backgroundColor: 'transparent' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => { onSectionDocumentsUpload(uploads) }}
          testId='saveDrawDocument'
          className={classes.button}
        >
          Confirm
        </Button>
      </Grid>
    </Grid>
  );
});

export const AddDrawDocumentDialog = observer(({ open, loanDetails, onClose, properties, drawList, drawSelectedView }: any) => {
  const { drawStore } = useDrawsStore();
  const classes = useStyles();
  const dialogStyles = useDialogContentStyles();
  return (
    <StandardDialog
      open={open}
      title={<span className={classes.title}>{drawSelectedView ? `Draw #${drawStore.currentDraw?.drawNumber}` : 'Add Document'}</span>}
      maxWidth="md"
      subTitle={<Typography className={classes.subtitle}>{drawSelectedView ? drawStore.currentDraw?.propertyAddress : 'Add a document to this loan.'}</Typography>}
      handleClose={onClose}
      dialogContentclasses={dialogStyles}
      dialogContent={
        <AddDocumentToDrawForm
          drawSelectedView={drawSelectedView}
          properties={properties}
          drawList={drawList}
          loanDetails={loanDetails}
          store={drawStore}
          handleClose={onClose} />
      }
    />)
});